import { format } from "date-fns";
import pdfmake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { calculateTotal } from "containers/lineItems/utils";
import { theme } from "styles/theme";
import type { TTargetCustomField } from "types/custom-field";
import type { TItemsContainer } from "types/items-container";
import type { TTargetProduct } from "types/product";
import { dollarFormatter } from "utils";
import { MultiplierReturnValues } from "utils/quote";
import type { Quote, QuoteCategory, BidClient } from "../index";
import { CustomField } from "./customFields";

type Props = {
  multipliers: MultiplierReturnValues[] | null;
  clients?: BidClient[] | null;
  data: Quote;
  total: any;
  multiplierTotal: { list_price: number; sale_price: number };
  dataUrl?: (dataUrl: string) => void;
  category?: { [catId: string]: QuoteCategory };
  customFields?: CustomField[] | null;
  /** Line items from `GET /api/v1/company/products/quote/:id` (same as Line Items UI). */
  lineItems?: TItemsContainer | null;
  /** Project division custom fields (`GET .../custom-fields/project/:projectId`). */
  projectCustomFields?: TTargetCustomField[] | null;
  output: "dataUrl" | "file";
};

/** Map API / loose rows to pdfmake-safe `{ key, value }` cells. */
export function normalizeCustomFieldsForPdf(
  fields: CustomField[] | null | undefined | unknown
): CustomField[] {
  if (!fields || !Array.isArray(fields)) return [];
  return fields
    .map((f) => {
      const row = f as Record<string, unknown>;
      return {
        id: String(row?.id ?? ""),
        key: String(row?.key ?? row?.custom_key ?? ""),
        value: String(row?.value ?? row?.custom_value ?? ""),
      };
    })
    .filter((f) => f.key.trim() !== "" || f.value.trim() !== "");
}

function quoteCustomFieldsFromPayload(
  data: Quote,
  explicit: CustomField[] | null | undefined
): CustomField[] {
  const fromExplicit = normalizeCustomFieldsForPdf(explicit);
  if (fromExplicit.length > 0) return fromExplicit;
  const q = data as unknown as Record<string, unknown>;
  return normalizeCustomFieldsForPdf(
    (Array.isArray(q.customFields) ? q.customFields : null) ??
      (Array.isArray(q.custom_fields) ? q.custom_fields : null)
  );
}

function projectCustomFieldsForPdf(
  rows: TTargetCustomField[] | null | undefined
): CustomField[] {
  if (!rows?.length) return [];
  return normalizeCustomFieldsForPdf(
    rows.map((cf) => ({
      id: `project-${cf.id}`,
      key: cf.custom_key,
      value: cf.custom_value ?? "",
    }))
  );
}

function primaryUnitRateKey(
  priceType: string
): keyof Pick<TTargetProduct, "list_price" | "unit_price" | "install_price"> {
  if (priceType === "sale") return "unit_price";
  if (priceType === "install") return "install_price";
  return "list_price";
}

function fourthColumnHeader(priceType: string): string {
  if (priceType === "install") return "INSTALL";
  if (priceType === "sale") return "SALE";
  return "LIST";
}

/** PDF table rows from v2 line items (products API). */
function buildPdfRowsFromLineItems(lineItems: TItemsContainer): {
  rows: unknown[];
  totals: { list_price: number; sale_price: number };
  priceType: string;
} {
  let products = [...(lineItems.products ?? [])];
  if (lineItems.hide_empty) {
    products = products.filter((p) => p.quantity);
  }

  const priceType = lineItems.price_type || "list";
  const rateKey = primaryUnitRateKey(priceType);
  const rows: unknown[] = [];

  products.forEach((product, i) => {
    const prev = i > 0 ? products[i - 1] : null;

    if (
      i === 0 ||
      (prev && prev.fk_estimate_option_id !== product.fk_estimate_option_id)
    ) {
      rows.push([
        {
          colSpan: 4,
          style: "lineItemsBand",
          text: product.estimate_option_label || "Additional Charges",
        },
        "",
        "",
        "",
      ]);
    }

    const showHeader =
      i === 0 ||
      (prev &&
        (product.fk_ingroup_id !== prev.fk_ingroup_id ||
          (!product.fk_ingroup_id && product.root_id !== prev.root_id)));

    if (showHeader) {
      const headerLabel =
        (product.fk_ingroup_id && (product.group_name as string | null)) ||
        product.root_name ||
        "Service";
      rows.push([
        {
          colSpan: 4,
          style: "tableSubHeader",
          text: String(headerLabel),
        },
        "",
        "",
        "",
      ]);
    }

    const qty = product.quantity ?? 0;
    const listUnit = +(product.list_price ?? 0);
    const saleUnit = +(product.unit_price ?? 0);
    const installUnit = +(product.install_price ?? 0);
    const primaryUnit =
      rateKey === "list_price"
        ? +(product.list_price ?? 0)
        : rateKey === "unit_price"
          ? +(product.unit_price ?? 0)
          : +(product.install_price ?? 0);


    rows.push([
      qty,
      {
        stack: [
          {
            text: [
              product.label ?? "",
              ...(product.long_description
                ? [
                    {
                      text: ` • ${product.long_description}`,
                      style: "defaultList",
                    },
                  ]
                : []),
            ],
          },
          {
            text: `List: ${dollarFormatter(listUnit)} | Sale: ${dollarFormatter(
              saleUnit
            )} | Install: ${dollarFormatter(installUnit)}`,
            style: "defaultList",
            margin: [0, 2, 0, 0],
          },
          ...(product.tech
            ? [
                {
                  text: `Added by ${product.tech}`,
                  style: "defaultList",
                  fontSize: 6,
                  margin: [0, 1, 0, 0],
                },
              ]
            : []),
        ],
      },
      dollarFormatter(listUnit * qty),
      dollarFormatter(primaryUnit * qty),
    ]);
  });

  const pt = calculateTotal(products);
  const sale_price =
    priceType === "list"
      ? pt.list
      : priceType === "sale"
        ? pt.sale
        : pt.install;

  return { rows, totals: { list_price: pt.list, sale_price }, priceType };
}

export const generatePDF = ({
  multipliers,
  data,
  clients,
  total,
  multiplierTotal,
  dataUrl,
  category,
  customFields,
  lineItems,
  projectCustomFields,
  output,
}: Props) => {
  const categoryMap = data.category ?? {};
  const clientsList = clients ?? [];
  const categoryLabels = category ?? categoryMap;

  const linePdf =
    lineItems?.products && lineItems.products.length > 0
      ? buildPdfRowsFromLineItems(lineItems)
      : null;
  const useLinePdf = linePdf && linePdf.rows.length > 0;
  const tableTotals = useLinePdf
    ? linePdf.totals
    : { list_price: total.list_price, sale_price: total.sale_price };
  const amountHeaderRight = useLinePdf
    ? fourthColumnHeader(linePdf.priceType)
    : "SALE";

  const styles = {
    headerLeft: {
      fontSize: 12,
      bold: true,
      color: theme.color.main,
    },
    headerSubLeft: {
      fontSize: 12,
      bold: true,
    },
    headerRight: {
      fontSize: 20,
      bold: true,
      alignment: "right",
    },
    headerSubRight: {
      fontSize: 12,
      bold: true,
      alignment: "right",
    },
    customField: {
      fontSize: 10,
    },
    tableHeader: {
      fontSize: 8,
      bold: true,
      margin: [0, 2, 3, 2],
      color: "white",
      fillColor: "#283747",
    },
    tableSubHeader: {
      bold: true,
      fontSize: 8,
      fillColor: theme.bg.blue,
    },
    defaultList: {
      fontSize: 7,
      color: "#2E4053",
    },
    nettTotal: {
      fontSize: 10,
      margin: [0, 5, 0, 5],
    },
    subTotal: {},
    lineItemsBand: {
      bold: true,
      fontSize: 8,
      fillColor: "#ffeee6",
      color: "#b45309",
    },
  };

  const defaultStyle = {
    columnGap: 10,
    fontSize: 8,
  };

  const layout = {
    defaultBorder: false,
    fillColor: (row: any) => row === 0 && theme.bg.blue,
  };

  const customFieldLayout = {
    defaultBorder: false,
  };

  const populateItems = Object.entries(categoryMap).reduce(
    (prev: any, [key, value]) => {
      const category = [
        {
          colSpan: 4,
          style: "tableSubHeader",
          text: value.label ?? "",
        },
        "",
        "",
        "",
      ];

      const items = Object.entries(value.items ?? {}).reduce(
        (prev: any, [key, value]) => {
          if (!value.quantity) return prev;
          return [
            ...prev,
            [
              value.quantity ?? "",
              {
                text: [
                  value.label ?? "",
                  "   ",
                  {
                    text: `List: ${dollarFormatter(value.list_price ?? 0)}${
                      value.list_price && value.sale_price ? " | " : ""
                    }Sale: ${dollarFormatter(value.sale_price ?? 0)}`,
                    style: "defaultList",
                  },
                ],
              },
              dollarFormatter((value.list_price ?? 0) * (value.quantity ?? 0)),
              dollarFormatter((value.sale_price ?? 0) * (value.quantity ?? 0)),
            ],
          ];
        },
        []
      );

      if (items.length > 0) {
        return [...prev, category, ...items];
      } else {
        return prev;
      }
    },
    []
  );

  const itemRows = useLinePdf ? linePdf.rows : populateItems;

  const populateMultipliers =
    multipliers?.map((multiplier) => {
      return [
        "",
        {
          stack: [
            {
              text: `${multiplier.label ?? ""} (${multiplier.rate ?? 0}%)`,
              alignment: "right",
            },
            {
              text: `${
                (categoryLabels &&
                  multiplier.fk_category_id?.reduce(
                    (prev, catId) =>
                      prev +
                      (categoryLabels[catId]?.label
                        ? `${categoryLabels[catId].label} | `
                        : ""),
                    ""
                  )) ||
                "All Category"
              }`,
              alignment: "right",
              style: "defaultList",
            },
          ],
        },
        multiplier.is_list ? dollarFormatter(multiplier.list_price) : "",
        multiplier.is_sale ? dollarFormatter(multiplier.sale_price) : "",
      ];
    }) ?? [];

  const mergedPdfCustomFields = [
    ...quoteCustomFieldsFromPayload(data, customFields),
    ...projectCustomFieldsForPdf(projectCustomFields),
  ];

  const populateCustomFields =
    mergedPdfCustomFields.length > 0
      ? mergedPdfCustomFields.map((customField) => {
          return [
            {
              text: customField.key ?? "",
              style: "customField",
            },
            {
              text: customField.value ?? "",
              style: "customField",
            },
          ];
        })
      : [
          [
            { text: "", style: "customField" },
            { text: "", style: "customField" },
          ],
        ];
  const finalPriceContainer = data.quote.total_amount
    ? {
        stack: [
          {
            text: dollarFormatter(data.quote.total_amount),
            fontWeight: "bold",
            fontSize: 16,
          },
          {
            text: clientsList.reduce((prev, client) => {
              if (!client) return prev;
              return `${prev && prev + ", "}${client.first_name} ${
                client.last_name
              }`;
            }, ""),
            margin: [0, 0, 0, 10],
          },
        ],
      }
    : (() => {
        const columns = clientsList
          .filter(Boolean)
          .map((client) => ({
            stack: [
              {
                text: `${client?.first_name ?? ""} ${client?.last_name ?? ""}`.trim(),
              },
              {
                text: dollarFormatter(client?.price ?? 0),
                fontWeight: "bold",
                fontSize: 16,
              },
            ],
          }));
        return {
          columns:
            columns.length > 0
              ? columns
              : [
                  {
                    stack: [{ text: "" }, { text: dollarFormatter(0) }],
                  },
                ],
          margin: [0, 0, 0, 10],
        };
      })();

  const content = [
    {
      columns: [
        {
          stack: [
            {
              text: data.quote.project_label ?? "",
              style: "headerLeft",
            },
            {
              text: data.quote.label ?? "",
              style: "headerSubLeft",
            },
          ],
        },
        {
          stack: [
            {
              text: `Quote #${data.quote.id ?? ""}`,
              style: "headerRight",
            },
            {
              text: format(new Date(), "MMM dd, yyyy"),
              style: "headerSubRight",
            },
          ],
        },
      ],
    },
    {
      layout,
      table: {
        headerRows: 1,
        widths: ["auto", "*", "auto", "auto"],
        body: [
          [
            {
              text: "QTY",
              style: "tableHeader",
            },
            {
              text: "DESCRIPTION",
              style: "tableHeader",
            },
            {
              text: "LIST",
              style: "tableHeader",
            },
            {
              text: amountHeaderRight,
              style: "tableHeader",
            },
          ],
          ...(itemRows as object[]),
          [
            {
              border: [false, true, false, false],
              text: "",
            },
            {
              border: [false, true, false, false],
              text: "SUBTOTAL",
              alignment: "right",
              style: "subTotal",
            },

            {
              border: [false, true, false, false],
              text: dollarFormatter(tableTotals.list_price),
            },
            {
              border: [false, true, false, false],
              text: dollarFormatter(tableTotals.sale_price),
            },
          ],
          ...populateMultipliers,
          [
            {
              border: [false, true, false, false],
              text: "",
            },
            {
              border: [false, true, false, false],
              text: "TOTAL",
              alignment: "right",
              style: "subTotal",
            },

            {
              border: [false, true, false, false],
              text: dollarFormatter(
                tableTotals.list_price + multiplierTotal.list_price
              ),
            },
            {
              border: [false, true, false, false],
              text: dollarFormatter(
                tableTotals.sale_price + multiplierTotal.sale_price
              ),
            },
          ],
        ],
      },
    },
    {
      text: "Final Price",
      style: "headerSubLeft",
    },
    finalPriceContainer,
    {
      text: "Custom Fields",
      style: "headerSubLeft",
    },
    {
      layout: customFieldLayout,
      table: {
        widths: ["auto", "*"],
        body: [...populateCustomFields],
      },
    },
  ];
  const docDefinition = {
    content,
    styles,
    defaultStyle,
  };

  pdfmake.vfs = pdfFonts.pdfMake.vfs;
  if (output === "dataUrl") {
    //  @ts-ignore
    pdfmake.createPdf(docDefinition).getDataUrl((url) => dataUrl(url));
  } else {
    pdfmake
      // @ts-ignore
      .createPdf(docDefinition)
      .download(data.quote.project_label + ".pdf");
  }
};
